/**
 * Chatbot Keywords and Capabilities Mapping
 * 
 * This file contains all the website capabilities, their keywords,
 * and routing information for the chatbot decision tree.
 */

export const CAPABILITIES = {
  // Core Creation Tools
  CREATE_PRESENTATION: {
    path: '/create-presentation',
    title: 'Create Slide Deck Presentation',
    description: 'Build PowerPoint-style presentations with 3D visuals',
    keywords: ['presentation', 'powerpoint', 'slides', 'slide deck', 'ppt', 'present', 'slideshow'],
    category: 'creation',
    expertLevel: 'expert',
    requiresAuth: true
  },
  
  SCROLLYTELLING: {
    path: '/scrollytelling/builder',
    title: 'Create Scrollytelling Story',
    description: 'Build interactive scroll-based narrative experiences',
    keywords: ['scrollytelling', 'story', 'narrative', 'scroll', 'interactive story', 'storytelling', 'scrolling'],
    category: 'creation',
    expertLevel: 'expert',
    requiresAuth: true
  },
  
  EXPLAINER_VIDEO: {
    path: '/explainer',
    title: 'Generate Explainer Video',
    description: 'Create AI-powered 3D biomedical explainer videos with narration',
    keywords: ['explainer', 'video', 'tour', 'narration', 'explain', 'educational video', 'animated', 'ai video'],
    category: 'creation',
    expertLevel: 'beginner',
    requiresAuth: true
  },
  
  UPLOAD_VISUAL: {
    path: '/dashboard/upload',
    title: 'Upload 3D Visual',
    description: 'Upload your own 3D models and visualizations',
    keywords: ['upload', '3d model', 'add visual', 'import', 'my content', 'custom visual', 'upload model'],
    category: 'management',
    expertLevel: 'intermediate',
    requiresAuth: true
  },
  
  // Specialized Tools
  MOLECULAR_VIEWER: {
    path: '/molecular-viewer',
    title: 'Molecular Viewer',
    description: 'Interactive 3D molecular visualization with AI chat',
    keywords: ['molecular', 'molecule', 'protein', 'chemistry', 'pymol', 'molecular structure', 'atoms', 'chemical'],
    category: 'tools',
    expertLevel: 'expert',
    requiresAuth: true
  },
  
  ORGAN_SEGMENTATION: {
    path: '/organ-segmentation',
    title: 'AI Organ Segmentation',
    description: 'Segment organs from medical imaging (CT/MRI) using AI',
    keywords: ['organ', 'segmentation', 'medical imaging', 'ct scan', 'mri', 'dicom', 'anatomy', 'radiology', 'ai segmentation'],
    category: 'tools',
    expertLevel: 'expert',
    requiresAuth: true
  },
  
  // Content Management
  DASHBOARD: {
    path: '/dashboard',
    title: 'My Dashboard',
    description: 'View and manage your content, presentations, and visuals',
    keywords: ['dashboard', 'my content', 'my work', 'manage', 'library', 'my visuals', 'my presentations'],
    category: 'management',
    expertLevel: 'all',
    requiresAuth: true
  },
  
  PROFILE: {
    path: '/dashboard/profile',
    title: 'My Profile',
    description: 'Manage your account settings and preferences',
    keywords: ['profile', 'account', 'settings', 'preferences', 'my account', 'user settings'],
    category: 'management',
    expertLevel: 'all',
    requiresAuth: true
  },
  
  BROWSE_VISUALS: {
    path: '/visuals',
    title: 'Browse Visual Library',
    description: 'Explore our library of 3D biomedical visualizations',
    keywords: ['browse', 'library', 'visuals', 'explore', 'catalog', 'gallery', 'search visuals', 'find visuals'],
    category: 'discovery',
    expertLevel: 'all',
    requiresAuth: false
  },
  
  TAGS: {
    path: '/tags',
    title: 'Organize with Tags',
    description: 'Tag and categorize your content',
    keywords: ['tags', 'organize', 'categorize', 'label', 'tag system'],
    category: 'management',
    expertLevel: 'intermediate',
    requiresAuth: true
  },
  
  // Information Pages
  TUTORIALS: {
    path: '/tutorials',
    title: 'Tutorials & Guides',
    description: 'Learn how to use Snorkle features',
    keywords: ['tutorial', 'help', 'guide', 'learn', 'how to', 'documentation', 'instructions'],
    category: 'learning',
    expertLevel: 'all',
    requiresAuth: false
  },
  
  USE_CASES: {
    path: '/usecases',
    title: 'Use Cases',
    description: 'See examples of what you can create',
    keywords: ['use cases', 'examples', 'showcase', 'what can i do', 'possibilities', 'sample'],
    category: 'learning',
    expertLevel: 'all',
    requiresAuth: false
  },
  
  ABOUT: {
    path: '/about',
    title: 'About Snorkle',
    description: 'Learn about Snorkle and our mission',
    keywords: ['about', 'who', 'company', 'mission', 'information'],
    category: 'information',
    expertLevel: 'all',
    requiresAuth: false
  },
  
  PRICING: {
    path: '/pricing',
    title: 'Pricing Plans',
    description: 'View subscription plans and pricing',
    keywords: ['pricing', 'plans', 'subscription', 'cost', 'price', 'upgrade', 'premium'],
    category: 'information',
    expertLevel: 'all',
    requiresAuth: false
  },
  
  CONTACT: {
    path: '/contact',
    title: 'Contact Us',
    description: 'Get in touch with our team',
    keywords: ['contact', 'support', 'help', 'email', 'reach out', 'customer service'],
    category: 'information',
    expertLevel: 'all',
    requiresAuth: false
  },
  
  POWERPOINT_PLUGIN: {
    path: '/powerpoint',
    title: 'PowerPoint Add-in',
    description: 'Add Snorkle visuals directly to PowerPoint',
    keywords: ['powerpoint', 'plugin', 'add-in', 'extension', 'ppt plugin', 'microsoft'],
    category: 'tools',
    expertLevel: 'intermediate',
    requiresAuth: false
  },
  
  BUNDLES: {
    path: '/bundles',
    title: 'Visual Bundles',
    description: 'Explore curated collections of related visuals',
    keywords: ['bundles', 'collections', 'sets', 'packages', 'grouped visuals'],
    category: 'discovery',
    expertLevel: 'all',
    requiresAuth: false
  },
  
  ORGANIZATION: {
    path: '/organization/info',
    title: 'Organization Management',
    description: 'Manage your organization account and team',
    keywords: ['organization', 'team', 'org', 'company', 'enterprise', 'group'],
    category: 'management',
    expertLevel: 'expert',
    requiresAuth: true
  }
};

/**
 * Parses user text input to find matching capabilities
 * @param {string} text - User's input text
 * @returns {Array} - Array of matching capabilities, sorted by relevance
 */
export function parseUserIntent(text) {
  if (!text || typeof text !== 'string') return [];
  
  const normalizedText = text.toLowerCase().trim();
  const matches = [];
  
  // Check each capability for keyword matches
  Object.entries(CAPABILITIES).forEach(([key, capability]) => {
    let score = 0;
    
    // Check each keyword
    capability.keywords.forEach(keyword => {
      const normalizedKeyword = keyword.toLowerCase();
      
      // Exact phrase match (higher score)
      if (normalizedText.includes(normalizedKeyword)) {
        score += 10;
      }
      
      // Individual word matches
      const textWords = normalizedText.split(/\s+/);
      const keywordWords = normalizedKeyword.split(/\s+/);
      
      keywordWords.forEach(keywordWord => {
        if (textWords.some(textWord => textWord.includes(keywordWord) || keywordWord.includes(textWord))) {
          score += 3;
        }
      });
    });
    
    // Also check title for matches
    const titleWords = capability.title.toLowerCase().split(/\s+/);
    const textWords = normalizedText.split(/\s+/);
    
    titleWords.forEach(titleWord => {
      if (textWords.some(textWord => textWord.includes(titleWord) || titleWord.includes(textWord))) {
        score += 2;
      }
    });
    
    if (score > 0) {
      matches.push({ key, capability, score });
    }
  });
  
  // Sort by score (highest first) and return top 3
  return matches
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(match => ({ ...match.capability, capabilityKey: match.key }));
}

/**
 * Get suggested capabilities based on user type
 * @param {boolean} isExpert - Whether user is an expert
 * @param {boolean} isLoggedIn - Whether user is logged in
 * @returns {Array} - Array of suggested capabilities
 */
export function getSuggestedCapabilities(isExpert = false, isLoggedIn = false) {
  const suggestions = [];
  
  if (isExpert) {
    // Expert users - focus on presentations and storytelling
    suggestions.push(
      CAPABILITIES.CREATE_PRESENTATION,
      CAPABILITIES.SCROLLYTELLING,
      CAPABILITIES.MOLECULAR_VIEWER,
      CAPABILITIES.ORGAN_SEGMENTATION
    );
  } else {
    // Beginner users - focus on easy content creation
    suggestions.push(
      CAPABILITIES.EXPLAINER_VIDEO,
      CAPABILITIES.BROWSE_VISUALS,
      CAPABILITIES.USE_CASES,
      CAPABILITIES.TUTORIALS
    );
  }
  
  // Filter out auth-required options if not logged in
  return suggestions.filter(cap => !cap.requiresAuth || isLoggedIn);
}

/**
 * Get dashboard-specific quick actions
 * @returns {Array} - Array of dashboard actions
 */
export function getDashboardQuickActions() {
  return [
    {
      title: 'Create presentations or articles',
      description: 'Slide decks and scrolling stories',
      action: 'submenu',
      submenu: [
        {
          title: 'Slide Deck Presentation',
          path: '/create-presentation',
          description: 'PowerPoint-style slide decks for browsers',
          usageDescription: 'This page lets you build presentation slides with interactive 3D visuals. Add slides, choose visuals from the library, and customize each slide with text and voiceovers.',
          requiresAuth: true
        },
        {
          title: 'Scrollytelling Story',
          path: '/scrollytelling/builder',
          description: 'Interactive narrative',
          usageDescription: 'Create an interactive scroll-based story. Add sections with visuals that activate as users scroll. Start from scratch or with AI-assisted article summary. Perfect for telling complex scientific narratives in an engaging way.',
          requiresAuth: true
        }
      ],
      icon: '▶'
    },
    {
      title: 'Generate video content',
      description: 'AI-powered explainer videos',
      action: 'navigate',
      path: '/explainer',
      icon: '▶',
      requiresAuth: true,
      usageDescription: 'Describe what you want to explain, and AI will generate a narrated 3D video. Choose voice, language, and scientific depth. Great for educational content and quick explanations.'
    },
    {
      title: 'AI-Powered Science',
      description: 'Molecular viewer and organ segmentation',
      action: 'submenu',
      submenu: [
        {
          title: 'Molecular Viewer',
          path: '/molecular-viewer',
          description: 'Visualize proteins and molecules',
          usageDescription: 'Interactive 3D molecular visualization tool. Search for molecules by name, PDB, or Pubchem ID, use natural language to manipulate views, or enter basic PyMOL commands for advanced control.',
          requiresAuth: true
        },
        {
          title: 'Organ Segmentation',
          path: '/organ-segmentation',
          description: 'AI-powered medical imaging',
          usageDescription: 'Upload CT or MRI scans and use deep learning powered tools to automatically segment and visualize organs in 3D. Perfect for creating educational materials, <b>not for clinical diagnostic use</b>.',
          requiresAuth: true
        }
      ],
      icon: '▶'
    },
    {
      title: 'Create interactive visuals',
      description: 'Upload and share models, imagery, video, and more',
      action: 'navigate',
      path: '/dashboard/upload',
      icon: '▶',
      usageDescription: 'Upload your own 3D models, medical imaging, or microscopy data. We support GLB, OBJ, STL, DICOM, and more. Add annotations, voiceovers, and share with QR codes.',
      requiresAuth: true
    }
  ];
}

/**
 * Get homepage quick actions
 * @param {boolean} isLoggedIn - Whether user is logged in
 * @returns {Array} - Array of homepage actions
 */
export function getHomepageQuickActions(isLoggedIn = false) {
  const actions = [
    {
      title: 'Create presentations or articles',
      description: 'Slide decks and scrolling stories',
      action: 'submenu',
      submenu: [
        {
          title: 'Slide Deck Presentation',
          path: '/create-presentation',
          description: 'PowerPoint-style slide decks for browsers',
          usageDescription: 'This page lets you build presentation slides with interactive 3D visuals. Add slides, choose visuals from the library, and customize each slide with text and voiceovers.',
          requiresAuth: true
        },
        {
          title: 'Scrollytelling Story',
          path: '/scrollytelling/builder',
          description: 'Interactive narrative',
          usageDescription: 'Create an interactive scroll-based story. Add sections with visuals that activate as users scroll. Start from scratch or with AI-assisted article summary. Perfect for telling complex scientific narratives in an engaging way.',
          requiresAuth: true
        }
      ],
      icon: '▶'
    },
    {
      title: 'Generate video content',
      description: 'AI-powered explainer videos',
      action: 'navigate',
      path: '/explainer',
      icon: '▶',
      requiresAuth: true,
      usageDescription: 'Describe what you want to explain, and AI will generate a narrated 3D video. Choose voice, language, and scientific depth. Great for educational content and quick explanations.'
    },
    {
      title: 'AI-Powered Science',
      description: 'Molecular viewer and organ segmentation',
      action: 'submenu',
      submenu: [
        {
          title: 'Molecular Viewer',
          path: '/molecular-viewer',
          description: 'Visualize proteins and molecules',
          usageDescription: 'Interactive 3D molecular visualization tool. Search for molecules by name, PDB, or Pubchem ID, use natural language to manipulate views, or enter basic PyMOL commands for advanced control.',
          requiresAuth: true
        },
        {
          title: 'Organ Segmentation',
          path: '/organ-segmentation',
          description: 'AI-powered medical imaging',
          usageDescription: 'Upload CT or MRI scans and use deep learning powered tools to automatically segment and visualize organs in 3D. Perfect for creating educational materials, <b>not for clinical diagnostic use</b>.',
          requiresAuth: true
        }
      ],
      icon: '▶'
    },
    {
      title: 'Create interactive visuals',
      description: 'Upload and share models, imagery, video, and more',
      action: 'navigate',
      path: '/dashboard/upload',
      icon: '▶',
      usageDescription: 'Upload your own 3D models, medical imaging, or microscopy data. We support GLB, OBJ, STL, DICOM, and more. Add annotations, voiceovers, and share with QR codes.',
      requiresAuth: true
    }
  ];
  
  return actions;
}

/**
 * Detailed step-by-step instructions for each page
 */
export const PAGE_INSTRUCTIONS = {
  '/create-presentation': {
    title: 'Creating Slide Deck Presentations',
    steps: [
      'Click the "New Presentation" field at the top of the page to change title',
      'Click "Add Slide" to create addifional slides',
      'Add images using the "Add Image" button, or interactive visuals using the "Add Snorkle Visual" button',
      'Add text content using the text editor below the visual',
      'Use the preview button to see how your presentation will look',
      'Click "Save" to save your work. It will be added to your library',
      'When ready, click "Share" to get a shareable link or QR code'
    ],
    tips: [
      'For interactive visuals, you can display a QR code for a mobile-ready version by clicking the "Show QR Code" toggle',
      'Each slide can have multiple visuals arranged in different layouts',
      'Use the "Privacy" dropdown to control who can view your presentation'
    ]
  },
  
  '/scrollytelling/builder': {
    title: 'Building Scrollytelling Stories',
    steps: [
      'Start from scratch or click "AI Draft from PDF" at the top',
      'If starting from scratch, add a title and description',
      'If using AI drafting, carefully review text and story details',
      'Click "Add Section" to create story sections',
      'For each section, choose a visual from your library using "Select Visual"',
      'Write your narrative text in the text editor',
      'Preview your story using the "Preview in Viewer" button',
      'Click "Save" when done. It will be added to your library',
      'Use "Share" to get a link that others can scroll through'
    ],
    tips: [
      'The AI Draft feature can draft a scrollytelling story in any language, starting with a PDF',
      'Be sure to double check all AI-generated content'
    ]
  },
  
  '/explainer': {
    title: 'Generating Explainer Videos',
    steps: [
      'Type what you want to explain in the large text box (e.g., "How insulin regulates blood sugar")',
      'Select your preferred language from the language dropdown',
      'Choose a voice from the voice selector',
      'Optional: Adjust the "Scientific Depth" dropdown',
      'Click "Generate Explainer" button',
      'Wait for the AI to create your video (can take several minutes)',
      'Preview the generated video with narration'
    ],
    tips: [
      'More detailed descriptions produce better results',
      'You can regenerate with different voices or languages',
      'Be sure to report content or narration inaccuracies using the "Report AI Inaccuracies" button'
    ]
  },
  
  '/molecular-viewer': {
    title: 'Using the Molecular Viewer',
    steps: [
      'Type a molecule name (e.g., "ubiquitin") or PDB ID (e.g., "1ubq") in the prompt box',
      'Click "Get Started" or press Enter',
      'The molecule will load in the 3D viewer',
      'Use natural language commands like "zoom in on the active site"',
      'Or toggle "PyMOL Mode" for PyMOL commands (e.g., "show cartoon")',
      'Click and drag to rotate the molecule',
      'Scroll to zoom in and out',
      'Use the workspace panel on the left to manage loaded molecules',
      'Click the share button (bottom right) to export your scene'
    ],
    tips: [
      'You can load multiple molecules at once',
      'The AI assistant understands conversational language',
      'Try commands like "highlight residues 10-20" or "color by chain"'
    ]
  },
  
  '/organ-segmentation': {
    title: 'Using AI Organ Segmentation',
    steps: [
      'Click "Upload Scan" button',
      'Select a CT or MRI file (DICOM format supported)',
      'Click "Start Segmentation" to begin AI processing',
      'Wait for the segmentation to complete (1-3 minutes)',
      'View the 3D reconstructed organs on the right',
      'Toggle different organs on/off using the organ list',
      'Use the slice viewer on the left to see cross-sections',
      'Adjust transparency and colors using the controls',
      'Click "Export" to download the 3D model',
      'Or "Share" to get a shareable visualization'
    ],
    tips: [
      'Best results with high-quality CT/MRI scans',
      'You can adjust the segmentation threshold if needed',
      'Toggle between split view and 3D-only view',
      '<b>Not for clinical diagnostic use</b> - educational purposes only'
    ]
  },
  
  '/dashboard/upload': {
    title: 'Uploading Your Own Content',
    steps: [
      'Click the "Upload" button or drag files into the upload area',
      'Select your file(s) - supported formats: GLB, OBJ, STL, DICOM, TIFF stacks',
      'Fill in the title and description for your visual',
      'Optional: Add tags to organize your content',
      'Choose privacy settings (private, unlisted, or public)',
      'Click "Upload" to process your file',
      'Wait for processing to complete',
      'Your visual will appear in your dashboard',
      'Click on it to add annotations, voiceovers, or share'
    ],
    tips: [
      'Larger files may take a few minutes to process',
      'You can upload multiple files at once',
      'Add descriptive tags to make content easier to find later'
    ]
  },
  
  '/visuals': {
    title: 'Browsing the Visual Library',
    steps: [
      'Use the search bar at the top to find specific content',
      'Or browse by category using the filter dropdowns',
      'Click on any visual thumbnail to see details',
      'Use the preview to rotate and inspect the 3D model',
      'Click "Add to My Library" to save it for later use',
      'Or click "Use in Presentation" to add directly to a project',
      'Filter by tags, anatomy, or content type on the left sidebar'
    ],
    tips: [
      'Save your favorite visuals for quick access later',
      'You can request custom visuals through the contact form',
      'All library visuals are scientifically verified'
    ]
  },
  
  '/usecases': {
    title: 'Exploring Use Cases',
    steps: [
      'Scroll through the different use case categories',
      'Click on any card to see a detailed example',
      'Watch demo videos to see features in action',
      'Click "Try This Yourself" to open the relevant tool',
      'Use the "Contact Us" button to discuss custom solutions'
    ],
    tips: [
      'Use cases show real-world applications',
      'Great place to get inspiration for your own projects',
      'Many examples link to tutorials for step-by-step guidance'
    ]
  }
};

/**
 * Get detailed instructions for a specific page
 * @param {string} path - Page path
 * @returns {Object|null} - Page instructions or null if not found
 */
export function getPageInstructions(path) {
  return PAGE_INSTRUCTIONS[path] || null;
}

const chatbotHelpers = {
  CAPABILITIES,
  parseUserIntent,
  getSuggestedCapabilities,
  getDashboardQuickActions,
  getHomepageQuickActions,
  PAGE_INSTRUCTIONS,
  getPageInstructions
};

export default chatbotHelpers;

